import { env } from './env';

type Config = {
  deployEnvironment: string;
  managementOrigin: string;
  apiGateway: string;
  asrPlexOrigin: string;
  nvoqWs: string;
  nvoqWsMaximumConnections: number;
  segmentAppId: string;
  splitioApiKey: string;
};

type EnvMapping = {
  local: Config;
  development: Config;
  staging: Config;
  production: Config;
  preview: (arg1: string) => Config; // Special case to allow namespaced previews,
  sironaPreview: (arg1: string) => Config; // Special case to allow namespaced previews
};

const OKTETO_NAMESPACE = env.OKTETO_NAMESPACE || '';
export const SIRONA_PREVIEW: string = env.SIRONA_PREVIEW || '';
const envMapping: EnvMapping = {
  local: {
    deployEnvironment: 'local',
    managementOrigin: 'http://localhost:3042',
    apiGateway: 'http://localhost:4000',
    asrPlexOrigin: 'ws://localhost:4000',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey:
      env.NODE_ENV === 'test' || env.LOCAL_SPLITS === 'true'
        ? 'localhost'
        : 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  },
  preview: (oktetoNamespace: string) => ({
    deployEnvironment: 'preview',
    managementOrigin: `https://admin-${oktetoNamespace}.preview.dev.sironamedical.com`,
    apiGateway: `https://graphql-${oktetoNamespace}.preview.dev.sironamedical.com`,
    asrPlexOrigin: 'wss://asr-plex.dev.sironamedical.com',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  }),
  sironaPreview: (sironaPreview: string) => ({
    deployEnvironment: 'preview',
    managementOrigin: `https://admin-${sironaPreview}.preview.dev.sironamedical.com`,
    apiGateway: `https://graphql-${sironaPreview}.dev.sironamedical.com`,
    asrPlexOrigin: 'wss://asr-plex.dev.sironamedical.com',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  }),
  development: {
    deployEnvironment: 'development',
    // Fallbacks for when the app is served from a non-app.* host in this deploy env (e.g. a
    // Docker/e2e build tested via localhost) — real app.* hosts derive these dynamically instead.
    managementOrigin: 'https://app.dev.sironamedical.com',
    apiGateway: 'https://graphql.dev.sironamedical.com',
    asrPlexOrigin: 'wss://asr-plex.dev.sironamedical.com',
    nvoqWs: 'wss://test.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 3,
    segmentAppId: 'Y3YGaLukzPdNWPXEjlGjea3aJso2Y0GG',
    splitioApiKey: 'ulc2iijia2jp42b2mf1l600k61c42iufnmav',
  },
  staging: {
    deployEnvironment: 'staging',
    managementOrigin: 'https://app.stg.sironamedical.com',
    apiGateway: 'https://graphql.stg.sironamedical.com',
    asrPlexOrigin: 'wss://asr-plex.stg.sironamedical.com',
    nvoqWs: 'wss://healthcare.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 7,
    segmentAppId: 'fHPBx5PtUtAR3Si5Fr6m8BurpFGS8Grv',
    splitioApiKey: '69fu9r39ra5d4914kd7g27p246i5gsvhn3ko',
  },
  production: {
    deployEnvironment: 'production',
    managementOrigin: 'https://app.sironamedical.com',
    apiGateway: 'https://graphql.prod.sironamedical.com',
    asrPlexOrigin: 'wss://asr-plex.prod.sironamedical.com',
    nvoqWs: 'wss://healthcare.nvoq.com/wsapi/v2/dictation/topics/radiology',
    nvoqWsMaximumConnections: 7,
    segmentAppId: 'PAwhfXbi6zGSCvgr5I8hC9kF3ZphVj0g',
    splitioApiKey: 'behco42hidesjj8f2n86lco36qvui88fvi7s',
  },
};

const getConfigFromDeployEnv = () => {
  switch (env.DEPLOY_ENV) {
    case 'production':
      return envMapping.production;
    case 'staging':
      return envMapping.staging;
    case 'development':
      return envMapping.development;
    case 'preview':
      return SIRONA_PREVIEW
        ? envMapping.sironaPreview(SIRONA_PREVIEW)
        : envMapping.preview(OKTETO_NAMESPACE);
    default:
      return envMapping.local;
  }
};

const config: Config = getConfigFromDeployEnv();

// If a environment variable is provided, we try to parse and use it
const envOrFallback = <T>(env: Nullable<string | T>, fallback: T): T => {
  if (env != null) {
    if (typeof env === 'string') {
      try {
        return JSON.parse(env);
      } catch (e: any) {
        return env as T;
      }
    } else {
      return env;
    }
  }
  return fallback;
};

// Derives a sibling service's origin from the current page's `app.*` hostname by swapping in
// `subdomain` and `scheme`, e.g. app.stg.sironamedical.com → https://graphql.stg.sironamedical.com.
// Falls back to `fallback` for hostnames that aren't a real app host (localhost, preview
// namespaces), since those don't follow the shared subdomain convention.
function deriveServiceOrigin(
  subdomain: string,
  scheme: 'https' | 'wss',
  fallback: string,
  hostname: string
): string {
  if (!hostname.startsWith('app.')) {
    return fallback;
  }

  const withSubdomain = `${scheme}://` + hostname.replace(/^app\./, `${subdomain}.`);

  // Dev/stg hostnames already carry the env segment (app.dev.*, app.us.dev.*, app.stg.*, …)
  // so the simple app. → {subdomain}. replace is sufficient.
  // Everything else is a production hostname (app.sironamedical.com, app.us.sironamedical.com, …)
  // which has no env segment, so we insert 'prod.' before the main domain.
  if (hostname.endsWith('.dev.sironamedical.com') || hostname.endsWith('.stg.sironamedical.com')) {
    return withSubdomain;
  } else {
    return withSubdomain.replace(/sironamedical\.com$/, 'prod.sironamedical.com');
  }
}

export function deriveGraphqlOrigin(
  hostname = typeof location !== 'undefined' ? location.hostname : ''
): string {
  return deriveServiceOrigin('graphql', 'https', config.apiGateway ?? '', hostname);
}

export function deriveAsrPlexOrigin(
  hostname = typeof location !== 'undefined' ? location.hostname : ''
): string {
  return deriveServiceOrigin('asr-plex', 'wss', config.asrPlexOrigin ?? '', hostname);
}

export function deriveManagementOrigin(
  hostname = typeof location !== 'undefined' ? location.hostname : ''
): string {
  // localhost and preview environments (e.g. app-{ns}.preview.dev.sironamedical.com) don't
  // start with 'app.' — fall back to the static config for those. Real app hostnames serve
  // the admin app on the same origin, under /manage/*.
  if (!hostname.startsWith('app.')) {
    return config.managementOrigin ?? '';
  }
  return `https://${hostname}`;
}

const graphqlOrigin: string = envOrFallback(env.API_GATEWAY_URL, deriveGraphqlOrigin());
const GRAPHQL_URL: string = envOrFallback(env.GRAPHQL_ENDPOINT, `${graphqlOrigin}/graphql`);

const managementOrigin: string = deriveManagementOrigin();
const ADMIN_URL: string = envOrFallback(env.ADMIN_URL, `${managementOrigin}/manage/users`);
const EXAM_MANAGER_URL: string = envOrFallback(
  env.EXAM_MANAGER_URL,
  `${managementOrigin}/manage/exam`
);
const ORDER_MANAGER_URL: string = envOrFallback(
  env.ORDER_MANAGER_URL,
  `${managementOrigin}/manage/order`
);
const PATIENT_MANAGER_URL: string = envOrFallback(
  env.PATIENT_MANAGER_URL,
  `${managementOrigin}/manage/patient`
);

const GRAPHQL_WS_URL: string = envOrFallback(
  env.GRAPHQL_WS_ENDPOINT,
  `${graphqlOrigin.replace(/^http/, 'ws')}/graphql`
);

const asrPlexOrigin: string = deriveAsrPlexOrigin();
const ASR_PLEX_WS_URL: string = envOrFallback(
  env.ASR_PLEX_WS_URL,
  `${asrPlexOrigin}/ws/transcribe`
);
const ASR_PLEX_VAD_WS_URL: string = envOrFallback(
  env.ASR_PLEX_VAD_WS_URL,
  `${asrPlexOrigin}/ws/vad`
);

export const DEPLOY_ENV: string = config.deployEnvironment;
export const NVOQ_WS_URL: string = envOrFallback(env.NVOQ_WS_URL, config.nvoqWs);
export const NVOQ_WS_MAXIMUM_CONNECTIONS: number = envOrFallback(
  env.NVOQ_WS_MAXIMUM_CONNECTIONS,
  config.nvoqWsMaximumConnections
);
export const SEGMENT_APP_ID = config.segmentAppId;
export const SPLITIO_API_KEY = config.splitioApiKey;
export const REPORTER_DEBUG: string = envOrFallback(env.REPORTER_DEBUG, 'false');
export const DATADOG_APP_VERSION: string = env.GIT_TAG ?? '?.?.?';
export const LOG_LEVEL: string = envOrFallback(env.LOG_LEVEL, 'info');

export type ClusterConnections = {
  apiGatewayUrl: string;
  graphqlUrl: string;
  graphqlWs: string;
  asrPlexWs: string;
  asrPlexVadWs: string;
};

export type AdminClusterUrls = {
  adminUrl: string;
  examManagerUrl: string;
  orderManagerUrl: string;
  patientManagerUrl: string;
};

export const MAIN_CLUSTER_CONNECTIONS = {
  apiGatewayUrl: graphqlOrigin,
  graphqlUrl: GRAPHQL_URL,
  graphqlWs: GRAPHQL_WS_URL,

  asrPlexWs: ASR_PLEX_WS_URL,
  asrPlexVadWs: ASR_PLEX_VAD_WS_URL,
};

/**
 * Calculates the default urls for the cluster based on the hostname.
 * Prefer using `DEFAULT_CLUSTER_CONNECTIONS`. This function is exported for testing.
 */
export function defaultClusterConnections(): ClusterConnections {
  return MAIN_CLUSTER_CONNECTIONS;
}

export let DEFAULT_CLUSTER_CONNECTIONS = defaultClusterConnections();
export function __test__resetDefaultClusterConnections() {
  DEFAULT_CLUSTER_CONNECTIONS = defaultClusterConnections();
}

const MAIN_CLUSTER_ADMIN_URLS = {
  adminUrl: ADMIN_URL,
  examManagerUrl: EXAM_MANAGER_URL,
  orderManagerUrl: ORDER_MANAGER_URL,
  patientManagerUrl: PATIENT_MANAGER_URL,
};

/**
 * Calculates the default admin urls for the cluster based on the hostname.
 * Prefer using `DEFAULT_CLUSTER_ADMIN_URLS`. This function is exported for testing.
 */
export function defaultAdminClusterUrls(): AdminClusterUrls {
  return MAIN_CLUSTER_ADMIN_URLS;
}

/**
 * Returns a set of urls for linking to the Admin app.
 * Currently, there is only one set of urls, but new regions
 * may get different urls in the future.
 */
export const DEFAULT_CLUSTER_ADMIN_URLS = defaultAdminClusterUrls();

/**
 * Returns a set of urls for external connections.
 * Currently, there is only one set of connections, but new regions
 * may get different urls in the future.
 */
export function getConnectionUrls(): ClusterConnections {
  return DEFAULT_CLUSTER_CONNECTIONS;
}
